/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Targeting editors and summaries. Hosts dimension output and sentiment
 * until those get their own modules.
 */
import {NgModule} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {TabsModule} from 'ngx-bootstrap/tabs';

import {AppSharedModule} from '../common/app-shared.module';
import {ReachAnalysisComponent} from '../common/controller/reach-analysis.component';
import {AdnSelectModule} from '../common/directives/adn-select/adn-select.module';
import {ChartsModule} from '../common/directives/charts.module';
import {FormControlsModule} from '../common/directives/form-controls/form-controls.module';
import {ResourceStatusModule} from '../common/directives/resource-status.module';
import {DimensionOutputComponent} from '../ad-unit/dimension-output-component';
import {SentimentResultComponent} from '../common/controller/sentiment-result-component';
import {TargetingEditorModalComponent} from './targeting-editor-modal.component';

import {KeywordFrequencyComponent} from './targeting/keyword-frequency-component';
import {AdUnitSummaryComponent} from './summary/adunit-summary.component';
import {AdUnitMatchingLabelSummaryComponent} from './summary/ad-unit-matching-label-summary.component';
import {CategorySummaryComponent} from './summary/category-summary.component';
import {DateTargetingSummaryComponent} from './summary/date-targeting-summary.component';
import {DayPartingSummaryComponent} from './summary/day-parting-summary.component';
import {DeviceSummaryComponent} from './summary/device-summary.component';
import {DomainSummaryComponent} from './summary/domain-summary.component';
import {GeospatialSummaryComponent} from './summary/geospatial-summary.component';
import {IpAddressSummaryComponent} from './summary/ip-address-summary.component';
import {KeyvalueSummaryComponent} from './summary/keyvalue-summary.component';
import {KeywordSummaryComponent} from './summary/keyword-summary.component';
import {NamedLocationSummaryComponent} from './summary/named-location-summary.component';
import {PublisherSummaryComponent} from './summary/publisher-summary.component';
import {RetargetingSummaryComponent} from './summary/retargeting-summary.component';
import {SegmentSummaryComponent} from './summary/segment-summary.component';
import {SemanticSummaryComponent} from './summary/semantic-summary.component';
import {SimilarKeywordSummaryComponent} from './summary/similar-keyword-summary.component';
import {SiteSummaryComponent} from './summary/site-summary.component';
import {SiteCountrySummaryComponent} from './summary/site-country-summary.component';
import {SiteGroupSummaryComponent} from './summary/site-group-summary.component';
import {ThirdPartyAudienceSummaryComponent} from './summary/third-party-audience-summary.component';
import {UrlSummaryComponent} from './summary/url-summary.component';
import {ViewabilitySummaryComponent} from './summary/viewability-summary.component';
import {WeatherSummaryComponent} from './summary/weather-summary.component';
import {ViewabilityTargetingComponent} from './targeting/viewability-targeting.component';
import {IpAddressTargetingComponent} from './targeting/ip-address-targeting.component';
import {SiteGroupTargetingComponent} from './targeting/site-group-targeting.component';
import {AdUnitMatchingLabelTargetingComponent} from './targeting/ad-unit-matching-label-targeting.component';
import {KeywordTargetingComponent} from './targeting/keyword-targeting.component';
import {CategoryTargetingComponent} from './targeting/category-targeting.component';
import {KeyValueTargetingComponent} from './targeting/key-value-targeting.component';
import {RetargetingTargetingComponent} from './targeting/retargeting-targeting.component';
import {DomainTargetingComponent} from './targeting/domain-targeting.component';
import {AdUnitTargetingComponent} from './targeting/adunit-targeting.component';
import {SiteTargetingComponent} from './targeting/site-targeting.component';
import {DeviceTargetingComponent} from './targeting/device-targeting.component';
import {PublisherTargetingComponent} from './targeting/publisher-targeting.component';
import {GeospatialTargetingComponent} from './targeting/geospatial-targeting.component';
import {NamedLocationTargetingComponent} from './targeting/named-location-targeting.component';
import {UrlTargetingComponent} from './targeting/url-targeting.component';
import {WeatherTargetingComponent} from './targeting/weather-targeting.component';
import {DateTargetingComponent} from './targeting/date-targeting.component';
import {DayPartingTargetingComponent} from './targeting/day-parting-targeting.component';
import {SiteCountryTargetingComponent} from './targeting/site-country-targeting.component';
import {SimilarKeywordTargetingComponent} from './targeting/similar-keyword-targeting.component';
import {SemanticTargetingComponent} from './targeting/semantic-targeting.component';
import {SegmentTargetingComponent} from './targeting/segment-targeting.component';
import {ThirdPartyAudienceTargetingComponent} from './targeting/third-party-audience-targeting.component';
import {TargetingTemplateComponent} from './targeting/targeting-template.component';
import {TargetingTemplateEditFormComponent} from './targeting/targeting-template-edit-form.component';
import {MatchingGroupsComponent} from './matching-groups.component';
import {CopyTargetingTemplatesComponent} from './copy-targeting-templates.component';
import {CopyFromLineItemComponent} from './copy-from-line-item.component';
import {TargetSummaryWrapperComponent} from './target-summary-wrapper.component';
import {TargetSummaryComponent} from './target-summary.component';
import {TargetSuperSummaryComponent} from './target-super-summary.component';
import {TargetSuperSummaryDetailModalComponent} from './target-super-summary-detail-modal.component';

const TARGETING_COMPONENTS = [
  KeywordFrequencyComponent,
  AdUnitSummaryComponent,
  AdUnitMatchingLabelSummaryComponent,
  CategorySummaryComponent,
  DateTargetingSummaryComponent,
  DayPartingSummaryComponent,
  DeviceSummaryComponent,
  DomainSummaryComponent,
  GeospatialSummaryComponent,
  IpAddressSummaryComponent,
  KeyvalueSummaryComponent,
  KeywordSummaryComponent,
  NamedLocationSummaryComponent,
  PublisherSummaryComponent,
  RetargetingSummaryComponent,
  SegmentSummaryComponent,
  SemanticSummaryComponent,
  SimilarKeywordSummaryComponent,
  SiteSummaryComponent,
  SiteCountrySummaryComponent,
  SiteGroupSummaryComponent,
  ThirdPartyAudienceSummaryComponent,
  UrlSummaryComponent,
  ViewabilitySummaryComponent,
  WeatherSummaryComponent,
  ViewabilityTargetingComponent,
  IpAddressTargetingComponent,
  SiteGroupTargetingComponent,
  AdUnitMatchingLabelTargetingComponent,
  KeywordTargetingComponent,
  CategoryTargetingComponent,
  KeyValueTargetingComponent,
  RetargetingTargetingComponent,
  DomainTargetingComponent,
  AdUnitTargetingComponent,
  SiteTargetingComponent,
  DeviceTargetingComponent,
  PublisherTargetingComponent,
  GeospatialTargetingComponent,
  NamedLocationTargetingComponent,
  UrlTargetingComponent,
  WeatherTargetingComponent,
  DateTargetingComponent,
  DayPartingTargetingComponent,
  SiteCountryTargetingComponent,
  SimilarKeywordTargetingComponent,
  SemanticTargetingComponent,
  SegmentTargetingComponent,
  ThirdPartyAudienceTargetingComponent,
  TargetingTemplateComponent,
  TargetingTemplateEditFormComponent,
  MatchingGroupsComponent,
  CopyTargetingTemplatesComponent,
  CopyFromLineItemComponent,
  TargetSummaryWrapperComponent,
  TargetSummaryComponent,
  TargetSuperSummaryComponent,
  TargetSuperSummaryDetailModalComponent,
  DimensionOutputComponent,
  SentimentResultComponent,
  ReachAnalysisComponent,
  TargetingEditorModalComponent
];

@NgModule({
  imports: [
    AppSharedModule,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ButtonsModule,
    CollapseModule,
    PopoverModule,
    TabsModule,
    AdnSelectModule,
    ChartsModule,
    FormControlsModule,
    ResourceStatusModule
  ],
  declarations: TARGETING_COMPONENTS,
  exports: TARGETING_COMPONENTS
})
export class TargetingModule {
}
